"use client";

import React, { createContext, useCallback, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useUniversalAuth, useAuth } from "@wtc/ui";
import { transformConnectionsToInboxes, createInboxGroups } from "@/lib/utils/provider-config";
import type { ConnectionsContextType } from "./types";
import { fetchConnections } from "./api";

export const ConnectionsContext = createContext<ConnectionsContextType | undefined>(
  undefined
);

export function ConnectionsProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();
  const { isAuthenticated } = useUniversalAuth();
  const { getAuthToken } = useAuth();
  const token = getAuthToken();

  const {
    data: connections = [],
    isLoading: connectionsLoading,
    error: connectionsError,
    refetch: refetchConnections,
  } = useQuery({
    queryKey: ["connections"],
    queryFn: () => fetchConnections(token || undefined),
    enabled: isAuthenticated,
    staleTime: 30 * 60 * 1000,
    refetchInterval: 30 * 60 * 1000,
  });

  const inboxes = transformConnectionsToInboxes(connections);
  const inboxGroups = createInboxGroups(inboxes);

  const createConnectionMutation = useMutation({
    mutationFn: async (connectionData: any) => {
      const response = await fetch("/api/connections/connections", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(connectionData),
      });
      if (!response.ok) throw new Error("Failed to create connection");
      return response.json();
    },
    retry: 3,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["connections"] });
    },
  });

  const removeConnectionMutation = useMutation({
    mutationFn: async ({
      connectionId,
      providerConfigKey,
    }: {
      connectionId: string;
      providerConfigKey: string;
    }) => {
      const response = await fetch("/api/connections/connections", {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ connectionId, providerConfigKey }),
      });
      if (!response.ok) throw new Error("Failed to remove connection");
      return response.json();
    },
    retry: 3,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["connections"] });
    },
  });

  const updateConnectionMutation = useMutation({
    mutationFn: async ({ connectionId, updates }: { connectionId: string; updates: any }) => {
      const response = await fetch("/api/connections/connections", {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ connectionId, ...updates }),
      });
      if (!response.ok) throw new Error("Failed to update connection");
      return response.json();
    },
    retry: 3,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["connections"] });
    },
  });

  const getConnectionById = useCallback(
    (id: string) => connections.find((conn) => conn.id === id),
    [connections]
  );

  const getInboxById = useCallback(
    (id: string) => inboxes.find((inbox) => inbox.id === id),
    [inboxes]
  );

  const getInboxByConnectionId = useCallback(
    (connectionId: string) => inboxes.find((inbox) => inbox.connectionId === connectionId),
    [inboxes]
  );

  const value: ConnectionsContextType = useMemo(
    () => ({
      connections,
      connectionsLoading,
      connectionsError: connectionsError?.message || null,
      inboxes,
      inboxGroups,
      inboxesLoading: connectionsLoading,
      inboxesError: connectionsError?.message || null,
      refetchConnections: async () => {
        await refetchConnections();
      },
      createConnection: createConnectionMutation.mutateAsync,
      removeConnection: removeConnectionMutation.mutateAsync,
      updateConnection: updateConnectionMutation.mutateAsync,
      getConnectionById,
      getInboxById,
      getInboxByConnectionId,
    }),
    [
      connections,
      connectionsLoading,
      connectionsError,
      inboxes,
      inboxGroups,
      getConnectionById,
      getInboxById,
      getInboxByConnectionId,
      refetchConnections,
      createConnectionMutation.mutateAsync,
      removeConnectionMutation.mutateAsync,
      updateConnectionMutation.mutateAsync,
    ]
  );

  return (
    <ConnectionsContext.Provider value={value}>{children}</ConnectionsContext.Provider>
  );
}
